import isString from 'lodash/isString'
import qs from 'query-string'

import type { TikTokSoundsFiltersForm } from '@/modules/tiktok-research/sounds/ui/filters/validation.ts'
import { apiClient } from '@/utils/axios'
import { dateToString } from '@/utils/date-converter'

import type { PaginatedData, PostType, SoundType } from '../sounds/types'
import type {
  AddToListRequest,
  ContentTypeTagsList,
  ListMediaType,
  MoveItemsToOtherListOptions,
  RemoveItemsFromListOptions,
} from '../types'
import type { TikTokVideosFiltersForm } from '../videos/ui/filters/validation.ts'
import { type TikTokContentListType } from '../videos/ui/types'

const FETCH_SIZE = 10

export const getVideos = async (
  filters: TikTokVideosFiltersForm | undefined,
  page: number,
  signal: AbortSignal | undefined,
): Promise<PaginatedData<PostType>> => {
  let params = {}

  if (filters) {
    const orderBy = filters.priority === 'most-views' ? 'MostView' : 'Trending'

    params = {
      countries: filters.countries,
      tagIds: filters.contentType
        ? Object.values(filters.contentType)
            .flatMap((tag) => tag)
            .filter(isString)
        : [],
      minimumView: filters.statistics?.videoViews[0],
      maximumView: filters.statistics?.videoViews[1],
      minimumFollowers: filters.statistics?.tiktokFollowers[0],
      maximumFollowers: filters.statistics?.tiktokFollowers[1],
      from: dateToString(filters.date.from),
      end: dateToString(filters.date.to),
      orderBy,
      page: page - 1,
      size: FETCH_SIZE,
    }
  }

  const queryString = qs.stringify(params, {
    skipNull: true,
    skipEmptyString: true,
  })

  return (
    await apiClient.get(`/tiktok-research/posts?${queryString}`, { signal })
  ).data
}

export const addToList = ({
  itemIds,
  listId,
  mediaType,
}: AddToListRequest): Promise<void> => {
  const endpoint = `/tiktok-research/collections/${listId}/${mediaType}`

  return apiClient.post(endpoint, { itemIds })
}

export const createList = async (
  list: Omit<TikTokContentListType, 'id'>,
): Promise<TikTokContentListType> => {
  return (await apiClient.post('/tiktok-research/collections', list)).data
}

export const editList = async (
  list: TikTokContentListType,
): Promise<TikTokContentListType> => {
  return (await apiClient.put(`/tiktok-research/collections/${list.id}`, list))
    .data
}

export const deleteList = (id: number): Promise<void> => {
  return apiClient.delete(`/tiktok-research/collections/${id}`)
}

export const getSoundsList = async (
  filters: TikTokSoundsFiltersForm | undefined,
  page: number,
  signal: AbortSignal | undefined,
): Promise<PaginatedData<SoundType>> => {
  let params = {}

  if (filters) {
    const orderBy = filters.priority === 'most-posts' ? 'MostPost' : 'Trending'

    params = {
      countries: filters.countries,
      minimumPost: filters.statistics?.postedVideos[0],
      maximumPost: filters.statistics?.postedVideos[1],
      from: dateToString(filters.date.from),
      end: dateToString(filters.date.to),
      orderBy,
      page: page - 1,
      size: FETCH_SIZE,
    }
  }

  const queryString = qs.stringify(params, {
    skipNull: true,
    skipEmptyString: true,
  })

  return (
    await apiClient.get(`/tiktok-research/sounds?${queryString}`, {
      signal,
    })
  ).data
}

export const getSoundById = async (
  id: string,
  filters: TikTokVideosFiltersForm,
  page: number,
  signal: AbortSignal | undefined,
): Promise<{ posts: PaginatedData<PostType>; sound: SoundType }> => {
  let params = {}

  if (filters) {
    const orderBy = filters.priority === 'most-views' ? 'MostView' : 'Trending'

    params = {
      countries: filters.countries,
      tagIds: filters.contentType
        ? Object.values(filters.contentType)
            .flatMap((tag) => tag)
            .filter(isString)
        : [],
      minimumView: filters.statistics?.videoViews[0],
      maximumView: filters.statistics?.videoViews[1],
      minimumFollowers: filters.statistics?.tiktokFollowers[0],
      maximumFollowers: filters.statistics?.tiktokFollowers[1],
      from: dateToString(filters.date.from),
      end: dateToString(filters.date.to),
      orderBy,
      page: page - 1,
      size: FETCH_SIZE,
    }
  }

  const queryString = qs.stringify(params, {
    skipNull: true,
    skipEmptyString: true,
  })

  return (
    await apiClient.get(`/tiktok-research/sounds/${id}?${queryString}`, {
      signal,
    })
  ).data
}

export const getLists = async (): Promise<TikTokContentListType[]> => {
  return (await apiClient.get('/tiktok-research/collections')).data
}

export const getListById = async (
  id: string,
): Promise<
  TikTokContentListType &
    Record<'posts', PostType[]> &
    Record<'sounds', SoundType[]>
> => {
  return (await apiClient.get(`/tiktok-research/collections/${id}`)).data
}

export const removeItemsFromList = ({
  listId,
  itemsIds,
}: RemoveItemsFromListOptions): Promise<void> => {
  return apiClient.post(
    `/tiktok-research/collections/${listId}/remove`,
    itemsIds,
  )
}

export const moveItemsToOtherList = ({
  fromListId,
  toListId,
  itemsIds,
}: MoveItemsToOtherListOptions): Promise<void> => {
  return apiClient.post(
    `/tiktok-research/collections/${fromListId}/move/${toListId}`,
    {
      postIds: itemsIds.posts,
      soundIds: itemsIds.sounds,
    },
  )
}

export const getTags = async (): Promise<ContentTypeTagsList[]> => {
  return (await apiClient.get('/tiktok-research/tags')).data
}

export const getVideoSource = (postId: number): Promise<string> =>
  apiClient
    .get(`/tiktok-research/posts/${postId}/download`)
    .then((res) => res.data.url)

export const getListMedias = (
  listId: string,
  mediaType: ListMediaType,
  page: number,
): Promise<PaginatedData<SoundType | PostType>> => {
  const queryString = qs.stringify({
    page: page - 1,
    size: FETCH_SIZE,
  })

  return apiClient
    .get(`/tiktok-research/collections/${listId}/${mediaType}?${queryString}`)
    .then((res) => res.data)
}
